import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProject } from "./ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { MODULE_STATUS, SCOPE_META, SCOPE_ORDER, EXPANSION_STATUS, scopeLabel } from "@/lib/discovery";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Compass, FileBarChart, Building2, Calendar, User, Flag, ArrowRight, CheckCircle2, Circle, Clock, Layers, ChevronDown, Ban, Network, Mail, UserPlus, Trash2, Users } from "lucide-react";

export default function Overview() {
  const { project, refresh } = useProject();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === "admin" || user?.role === "consultant";
  const [disc, setDisc] = useState(null);

  const load = () => api.get(`/projects/${project.id}/discovery`).then((r) => setDisc(r.data));
  useEffect(() => { load(); }, [project.id]);

  const done = disc?.modules.filter((m) => ["validated", "customer_completed", "consultant_reviewed"].includes(m.status)).length || 0;

  return (
    <div className="px-6 py-8 lg:px-10">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left: progress + facts + scope */}
        <div className="space-y-6 lg:col-span-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-500">Overall Discovery</span>
              <span className="font-heading text-2xl font-extrabold text-sky-600">{project.progress}%</span>
            </div>
            <Progress value={project.progress} className="mt-3 h-2.5" />
            <div className="mt-3 text-xs text-slate-400">{done} of {disc?.modules.length || 10} modules substantially complete</div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wider text-slate-400">Project Facts</h3>
            <div className="space-y-3 text-sm">
              <Fact icon={Building2} label="Customer" value={project.customer_name} />
              <Fact icon={Flag} label="Implementation" value={project.implementation_type} />
              <Fact icon={User} label="Consultant" value={project.consultant_name || "Unassigned"} />
              <Fact icon={Calendar} label="Country" value={project.country || "—"} />
            </div>
          </div>

          {disc && <ScopeStrategyCard project={project} disc={disc} canManage={canManage} onSaved={() => { refresh(); load(); }} />}

          {canManage && <CustomerAccessCard projectId={project.id} projectName={project.project_name} />}

          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="overview-start-discovery" onClick={() => navigate(`/projects/${project.id}/discovery`)} className="rounded-lg bg-sky-600 hover:bg-sky-700">
              <Compass className="mr-1.5 h-4 w-4" /> Discovery
            </Button>
            <Button variant="outline" data-testid="overview-blueprint" onClick={() => navigate(`/projects/${project.id}/blueprint`)} className="rounded-lg">
              <FileBarChart className="mr-1.5 h-4 w-4" /> Blueprint
            </Button>
          </div>
        </div>

        {/* Right: module journey + scope expansions */}
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="mb-5 font-heading text-lg font-bold text-slate-900">Discovery Journey</h3>
            <div className="space-y-2.5">
              {disc?.modules.map((m) => {
                const st = MODULE_STATUS[m.status] || { label: m.status, tone: "slate" };
                const Icon = m.out_of_scope ? Ban : m.status === "validated" ? CheckCircle2 : m.status === "not_started" ? Circle : Clock;
                return (
                  <button key={m.key} data-testid={`overview-module-${m.key}`} onClick={() => navigate(`/projects/${project.id}/discovery/${m.key}`)}
                    className={"group flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors " + (m.out_of_scope ? "border-slate-200 bg-slate-50/60 opacity-75 hover:border-amber-300" : "border-slate-200 hover:border-sky-300 hover:bg-sky-50/40")}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white">{m.code}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-heading text-sm font-bold text-slate-800">{m.title}</div>
                        <StatusBadge label={SCOPE_META[m.scope]?.label || m.scope} tone={SCOPE_META[m.scope]?.tone || "slate"} />
                      </div>
                      {!m.out_of_scope && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <Progress value={m.progress} className="h-1.5 w-28" />
                          <span className="text-[11px] font-semibold text-slate-400">{m.answered}/{m.total}</span>
                        </div>
                      )}
                    </div>
                    {!m.out_of_scope && <StatusBadge label={st.label} tone={st.tone} />}
                    <Icon className="h-4 w-4 text-slate-300 group-hover:text-sky-500" />
                  </button>
                );
              })}
            </div>
          </div>

          {disc && <OrgChartCard disc={disc} />}

          {canManage && disc && <ExpansionsPanel projectId={project.id} flags={disc.scope_flags || []} onChanged={load} />}
        </div>
      </div>
    </div>
  );
}

function OrgChartCard({ disc }) {
  const org = disc.modules.find((m) => m.key === "org_structure");
  if (!org || org.out_of_scope) return null;
  const r = disc.responses || {};
  const sales = (r.os_sales_hierarchy || []).filter(Boolean);
  const service = (r.os_service_hierarchy || []).filter(Boolean);
  const teamsQ = org.questions.find((q) => q.key === "os_teams");
  const teamLbl = teamsQ ? Object.fromEntries((teamsQ.options || []).map((o) => [o.value, o.label])) : {};
  const teams = (r.os_teams || []).map((v) => teamLbl[v] || v);
  const managers = (r.os_team_managers || []).filter((row) => row && (row.manager || row.responsibility));
  if (!sales.length && !service.length && !teams.length && !managers.length) return null;
  return (
    <div data-testid="org-chart-card" className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2"><Network className="h-4 w-4 text-sky-500" /><h3 className="font-heading text-lg font-bold text-slate-900">Organization Chart</h3></div>
      <p className="mt-1 text-sm text-slate-500">Captured in Org &amp; User Structure and reused across discovery.</p>
      {(sales.length > 0 || service.length > 0) && (
        <div className="mt-5 grid gap-8 md:grid-cols-2">
          {sales.length > 0 && <HierarchyChain title="Sales" chain={sales} tone="sky" />}
          {service.length > 0 && <HierarchyChain title="Service / Operations" chain={service} tone="violet" />}
        </div>
      )}
      {teams.length > 0 && (
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Teams on Salesforce</div>
          <div className="mt-2 flex flex-wrap gap-1.5">{teams.map((t, i) => <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{t}</span>)}</div>
        </div>
      )}
      {managers.length > 0 && (
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Managers</div>
          <div className="mt-2 space-y-1.5">{managers.map((row, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-slate-700">{row.manager || "—"}</span>
              {row.responsibility && <><ArrowRight className="h-3.5 w-3.5 text-slate-300" /><span className="text-slate-500">{row.responsibility}</span></>}
            </div>
          ))}</div>
        </div>
      )}
    </div>
  );
}

function HierarchyChain({ title, chain, tone }) {
  const toneMap = { sky: "border-sky-200 bg-sky-50 text-sky-900", violet: "border-violet-200 bg-violet-50 text-violet-900" };
  return (
    <div>
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{title} hierarchy</div>
      <div className="flex flex-col items-center">
        {chain.map((role, i) => (
          <div key={i} className="flex w-full flex-col items-center">
            <div className={"w-full max-w-[220px] rounded-xl border px-4 py-2.5 text-center text-sm font-semibold shadow-sm " + toneMap[tone]}>{role}</div>
            {i < chain.length - 1 && <div className="h-5 w-px bg-slate-300" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScopeStrategyCard({ project, disc, canManage, onSaved }) {
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState(disc.scope_level || "standard_config");
  const [overrides, setOverrides] = useState(disc.module_scope || {});
  const [busy, setBusy] = useState(false);

  useEffect(() => { setLevel(disc.scope_level || "standard_config"); setOverrides(disc.module_scope || {}); }, [disc]);

  const setOverride = (mkey, val) => {
    setOverrides((o) => {
      const next = { ...o };
      if (val === "inherit") delete next[mkey];
      else next[mkey] = val;
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/scope`, { scope_level: level, module_scope: overrides });
      toast.success("Scope strategy updated");
      onSaved();
    } catch { toast.error("Could not save scope"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="scope-strategy-card">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-sky-500" />
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-400">Scope Strategy</h3>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-slate-500">Discovery depth is governed by the agreed implementation scope. Deeper questions appear only when the scope permits.</p>

      <div className="mt-4">
        <label className="text-xs font-semibold text-slate-500">Project default scope</label>
        {canManage ? (
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="scope-level-select" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="mt-1"><StatusBadge label={SCOPE_META[level]?.label || level} tone={SCOPE_META[level]?.tone || "slate"} /></div>
        )}
      </div>

      <button onClick={() => setOpen((o) => !o)} data-testid="scope-overrides-toggle" className="mt-4 flex w-full items-center justify-between text-xs font-semibold text-slate-600 hover:text-sky-600">
        <span>Per-module overrides</span>
        <ChevronDown className={"h-4 w-4 transition-transform " + (open ? "rotate-180" : "")} />
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {disc.modules.map((m) => (
            <div key={m.key} className="flex items-center justify-between gap-2">
              <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-600">{m.code} · {m.title}</span>
              {canManage ? (
                <Select value={overrides[m.key] || "inherit"} onValueChange={(v) => setOverride(m.key, v)}>
                  <SelectTrigger data-testid={`module-scope-${m.key}`} className="h-8 w-40 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit ({SCOPE_META[level].label})</SelectItem>
                    {SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}
                    <SelectItem value="out_of_scope">Out of Scope</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <StatusBadge label={SCOPE_META[m.scope]?.label || m.scope} tone={SCOPE_META[m.scope]?.tone || "slate"} />
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <Button data-testid="scope-save" onClick={save} disabled={busy} className="mt-4 w-full rounded-lg bg-slate-900 hover:bg-slate-800">
          {busy ? "Saving…" : "Save Scope Strategy"}
        </Button>
      )}
    </div>
  );
}

function ExpansionsPanel({ projectId, flags, onChanged }) {
  const classify = async (flag, status) => {
    try {
      await api.patch(`/projects/${projectId}/scope-flags/${flag.id}`, { status, note: flag.note || "" });
      toast.success("Scope expansion classified");
      onChanged();
    } catch { toast.error("Could not classify"); }
  };
  const pending = flags.filter((f) => f.status === "pending").length;
  const OPTIONS = [["approved_in_scope", "Approve In-Scope"], ["change_request", "Change Request"], ["future_backlog", "Future Backlog"]];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="expansions-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flag className="h-4 w-4 text-amber-500" />
          <h3 className="font-heading text-lg font-bold text-slate-900">Scope Expansions</h3>
        </div>
        {pending > 0 && <StatusBadge label={`${pending} pending`} tone="amber" />}
      </div>
      <p className="mt-1 text-sm text-slate-500">Requirements captured during discovery that may go beyond the agreed scope. Classify each so the blueprint reflects the right commitments.</p>

      {flags.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">No potential scope expansions have been raised yet.</div>
      ) : (
        <div className="mt-4 space-y-3">
          {flags.map((flag) => {
            const st = EXPANSION_STATUS[flag.status] || EXPANSION_STATUS.pending;
            return (
              <div key={flag.id} data-testid={`expansion-row-${flag.question_key}`} className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading text-sm font-bold text-slate-800">{flag.question_text || flag.question_key}</div>
                    <div className="mt-1 text-xs text-amber-800">Trigger: "{flag.trigger_label}" · implies {scopeLabel(flag.detected_scope)}</div>
                  </div>
                  <StatusBadge label={st.label} tone={st.tone} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {OPTIONS.map(([val, label]) => (
                    <Button key={val} size="sm" variant={flag.status === val ? "default" : "outline"} data-testid={`panel-classify-${flag.question_key}-${val}`}
                      onClick={() => classify(flag, val)}
                      className={"h-8 rounded-lg text-xs " + (flag.status === val ? "bg-amber-600 hover:bg-amber-700" : "")}>
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CustomerAccessCard({ projectId, projectName }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get(`/projects/${projectId}/participants`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true); setResult(null);
    try {
      const { data: res } = await api.post(`/projects/${projectId}/invite`, { email: email.trim(), name: name.trim() });
      setResult(res);
      if (res.email_sent) toast.success("Invitation email sent");
      else if (res.created_account) toast.success("Customer account created");
      else toast.success("Participant added");
      setEmail(""); setName("");
      load();
    } catch (e) { toast.error("Could not invite"); }
    finally { setBusy(false); }
  };

  const remove = async (em) => {
    try { await api.delete(`/projects/${projectId}/participants/${encodeURIComponent(em)}`); load(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="customer-access-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-sky-500" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-400">Customer Access</h3>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="invite-customer-btn" className="h-8 rounded-lg bg-sky-600 text-xs hover:bg-sky-700"><UserPlus className="mr-1 h-3.5 w-3.5" /> Invite</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite a customer participant</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-sm font-semibold">Name</Label><Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-lg" placeholder="Rajesh Kumar" /></div>
              <div><Label className="text-sm font-semibold">Email</Label><Input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-lg" placeholder="rajesh@company.com" /></div>
              {data && !data.email_enabled && (
                <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Email sending isn't configured yet, so no email will be sent. An account will be created and you'll get a temporary password to share manually.</div>
              )}
              {result && (
                <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-700" data-testid="invite-result">
                  {result.created_account ? <div>Account created for <strong>{result.email}</strong>.</div> : <div><strong>{result.email}</strong> already had an account.</div>}
                  {result.temp_password && <div className="mt-1">Temporary password: <strong>{result.temp_password}</strong> (share this with the customer).</div>}
                  <div className="mt-1">
                    {result.email_sent
                      ? "Invitation email sent."
                      : result.email_enabled
                        ? `Email could not be sent: ${result.email_error || "unknown error"}. Share the temporary password manually.`
                        : "No email sent (email not configured)."}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="invite-submit" onClick={invite} disabled={busy} className="rounded-lg bg-sky-600 hover:bg-sky-700">{busy ? "Inviting…" : "Send invite"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-slate-500">People who can sign in and complete this project's discovery.{data && !data.email_enabled && " Email invites are off — share the temporary password shown after inviting."}</p>
      <div className="mt-4 space-y-2">
        {(data?.participants || []).length === 0 && <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-xs text-slate-400">No customer participants yet.</div>}
        {(data?.participants || []).map((p) => (
          <div key={p.email} data-testid={`participant-${p.email}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2.5">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-700">{p.name || p.email}</div>
              <div className="truncate text-xs text-slate-400">{p.email}</div>
            </div>
            <div className="flex items-center gap-2">
              {p.delivery_status && (() => {
                const map = {
                  sent: ["Sent", "bg-sky-50 text-sky-600"],
                  delivered: ["Delivered", "bg-emerald-50 text-emerald-600"],
                  opened: ["Opened", "bg-emerald-50 text-emerald-700"],
                  clicked: ["Clicked", "bg-emerald-100 text-emerald-700"],
                  bounced: ["Bounced", "bg-red-50 text-red-600"],
                  dropped: ["Dropped", "bg-red-50 text-red-600"],
                  deferred: ["Deferred", "bg-amber-50 text-amber-600"],
                  spam: ["Spam", "bg-red-50 text-red-600"],
                  failed: ["Not sent", "bg-red-50 text-red-600"],
                  not_configured: ["No email", "bg-slate-100 text-slate-500"],
                };
                const [lbl, tone] = map[p.delivery_status] || [p.delivery_status, "bg-slate-100 text-slate-500"];
                return <span title={p.delivery_detail || ""} data-testid={`delivery-${p.email}`} className={"rounded-md px-2 py-0.5 text-[11px] font-bold " + tone}>{lbl}</span>;
              })()}
              <span className={"rounded-md px-2 py-0.5 text-[11px] font-bold " + (p.has_account ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600")}>{p.has_account ? "Has account" : "No account"}</span>
              <button onClick={() => remove(p.email)} data-testid={`remove-participant-${p.email}`} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function Fact({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0"><div className="text-xs text-slate-400">{label}</div><div className="font-semibold text-slate-700">{value}</div></div>
    </div>
  );
}
